/*
XOL-67 (decision 159, V1): the VERSIONED field save of the request and of an option, in pure functions (no store, no axios, no component; the one
import that is not pure data is lib/requestRowSync, whose helpers it reuses).

Carlos overrode "last write wins" (2026-10-28): request edits and option edits use XOL-596's per-field versioning, the way every other request
does (lib/requestRowSync, header; XOL-595 contract v3, the API's `FieldPatch`). The engine's rules are reused, not copied: `cloneValue`,
`isValidPath`, `checkWritablePath` and `confirmSaved` are requestRowSync's own. What an RTA adds is only WHICH paths there are.

  The body       { contentVersion, fields: { path: value }, base: { path: n } }; `base[path]` is the contentVersion of the response the field's
                 DISPLAYED value came from (G-BASE), never the row's newest version. A missing base defaults to contentVersion on the server,
                 so this module never leaves it out. One path per body, EXCEPT the point: latitude + longitude (the request) and actualLat +
                 actualLng (an option) go in ONE body with two paths and two bases (P-S27: "both or neither"). Two single-path saves fail the
                 pair rule with 422 on the first pin placement and leave a half-moved point.
  The request    PUT /actions/{action}   paths `state.nominal.<field>`   (rtaNominal.nominalPath)
  An option      PUT /candidate/{id}     paths are the option columns; `markers` is one path (a whole list). A non-RTA (legacy) option keeps
                 today's body: nothing here applies to it.
  The answer     carries `contentVersion` and `fieldVersions: { path: { v, by, org, at } }` on the action row and on option rows.

  fieldBody(version, path, value, base)        the body of one path, or null (no version, a base that is not a number, a path the API refuses)
  requestFieldBody(version, field, value, base)  fieldBody for a request field
  optionFieldBody(version, column, value, base, gates)  fieldBody for an option column the caller's lists allow (rtaOption.writableFields)
  fieldsBody(version, patches, bases)          the body of SEVERAL paths ([{ path, value }]), each with its own base; null when any one cannot be sent
  requestBodies(version, changes, bases)       rtaNominal.nominalChanges as the bodies to send, in order: [{ paths, body }], the point as ONE
  optionBodies(version, changes, bases, gates) rtaOption.contentChanges as the bodies to send, in order: [{ paths, body }], the point as ONE
  fieldPatches(changes)                        { key: value } (rtaOption.contentChanges) as [{ path, value }], one per key
  adoptBases(bases, paths, version)            a copy of the base map with each path's base moved to `version` (a field adopted from the server)
  seedBases(paths, version)                    the base map of a row just read: every path at the row's contentVersion
  savedBase(bases, path, sent, savedRow)       after a 2xx: { confirmed, version } for the path (requestRowSync.confirmSaved: the base moves only
                                               when the answer holds the value we sent)
  (the conflicts of a 409 stale_field, and their card, are lib/rtaRefusal.staleConflicts / staleNotice)

Refusal order on the server (P-S27, D159): 428 version_required; 422 read_only_fields; 422 invalid_value; unchanged paths skipped (a replay is
200 and writes nothing); 409 request_closed; 409 decision_locked; 403 not_allowed; 409 request_locked; 409 stale_field; then the write. After
the conflict check a duplicate Site Name / Site ID is 422 site_name_taken / site_id_taken, for a CHANGED value only. An option (P-S28): 404, 403,
409 invalid_state, 428, 422 (a path outside the caller's list is refused, not dropped), unchanged 200, 409 option_locked, 409 stale_field. A LOCK
answers before a conflict: lib/rtaRefusal.saveRefusal reads the lock codes first, so a refusal that could be either shows the lock.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { cloneValue, checkWritablePath, confirmSaved, isValidPath } from '@/lib/requestRowSync'
import { toAppTime, DATE_SQL_FORMAT } from '@/lib/appTime'
import { nominalPath, NOMINAL_FIELDS } from '@/lib/rtaNominal'
import { SERVER_OWNED_FIELDS, writableFields } from '@/lib/rtaOption'

function isNumber (v) {
  return typeof v === 'number' && isFinite(v)
}

// The point is two paths that travel together (P-S27 / P-S28 pair rule): both or neither, so one body carries both.
export var POINT_PAIRS = [['state.nominal.latitude', 'state.nominal.longitude'], ['actualLat', 'actualLng']]

// Dates go out as Y-m-d, through the app clock (requestRowSync's wireValue does the same for `targetDate` / `actualDate`; XOL-414: never a new parse).
// The option's own date column is `surveyDate`.
function wireValue (path, value) {
  var last = String(path).split('.').pop()

  if (last === 'surveyDate' && value !== undefined && value !== null && value !== '') {
    var m = toAppTime(value)

    return m === null ? cloneValue(value) : m.format(DATE_SQL_FORMAT)
  }

  return cloneValue(value)
}

/**
 * The body of ONE path (requestRowSync.buildFieldBody's shape): { contentVersion, fields: { path: value }, base: { path: n } }. Null when the row
 * has no version (a 428 would follow), when the field's own base is not a number (sending the newest version instead would bring a lost update
 * back, G-BASE), or when the path is not a valid v3 path.
 */
export function fieldBody (contentVersion, path, value, baseVersion) {
  return fieldsBody(contentVersion, [{ path: path, value: value, base: baseVersion }])
}

/**
 * The body of SEVERAL paths: `patches` [{ path, value, base }] where `base` is the field's own base version, or [{ path, value }] with `bases`
 * { path: n } beside it. Each path has its OWN base. Null when any one cannot be sent (see fieldBody) or there is none.
 */
export function fieldsBody (contentVersion, patches, bases) {
  var list = Array.isArray(patches) ? patches : []

  if (!isNumber(contentVersion) || list.length === 0) {
    return null
  }

  var fields = {}
  var base = {}

  for (var i = 0; i < list.length; i++) {
    var p = list[i]
    var b = p && p.base !== undefined ? p.base : (bases && p ? bases[p.path] : undefined)

    if (!p || !isNumber(b) || !isValidPath(p.path) || Object.prototype.hasOwnProperty.call(fields, p.path)) {
      return null
    }

    fields[p.path] = wireValue(p.path, p.value)
    base[p.path] = b
  }

  return { contentVersion: contentVersion, fields: fields, base: base }
}

// Can this request field be sent at all? A request field, on a path the action's ownership table does not refuse.
function requestPathOf (field) {
  if (NOMINAL_FIELDS.indexOf(field) === -1) {
    return null
  }

  var path = nominalPath(field)

  // The same static check the engine runs for an action row: `state` is a content column of an RTA, `state.nominal.*` is not server-owned.
  return checkWritablePath('action', path, 'RTA').ok ? path : null
}

// Can this option column be sent at all? On the caller's own list (the server REFUSES, 422 read_only_fields, a path outside it), and not server-owned.
function optionColumnOk (column, gates) {
  return SERVER_OWNED_FIELDS.indexOf(column) === -1 && writableFields(gates).indexOf(column) !== -1
}

/** The body of a request field (`PUT /actions/{id}`): null for a key that is not a request field, or a path the action's ownership table refuses. */
export function requestFieldBody (contentVersion, field, value, baseVersion) {
  var path = requestPathOf(field)

  return path === null ? null : fieldBody(contentVersion, path, value, baseVersion)
}

/**
 * The body of an option column (`PUT /candidate/{id}`). `gates` is the caller's rtaOption.writableFields input ({ tc, shared, clientRef }), so
 * a column outside the caller's list is never sent (the server answers it 422 read_only_fields); a server-owned column (SERVER_OWNED_FIELDS, the live
 * review, the distances) never is.
 */
export function optionFieldBody (contentVersion, column, value, baseVersion, gates) {
  return optionColumnOk(column, gates) ? fieldBody(contentVersion, column, value, baseVersion) : null
}

// Groups of patches that go in one body, in the order they came: a point pair whose BOTH paths changed is one group, anything else one each.
// (If only one of a pair changed, the server merges it over the stored other half, so it is sent alone.)
function groupPatches (patches) {
  var groups = []
  var used = {}

  patches.forEach(function (p) {
    if (used[p.path]) {
      return
    }

    var mate = null

    POINT_PAIRS.forEach(function (pair) {
      var i = pair.indexOf(p.path)

      if (i !== -1) {
        mate = pair[1 - i]
      }
    })

    var other = null

    if (mate !== null) {
      patches.forEach(function (q) { if (q.path === mate) { other = q } })
    }

    used[p.path] = true

    if (other) {
      used[other.path] = true
      // latitude before longitude, whichever came first
      groups.push(POINT_PAIRS.some(function (pair) { return pair[0] === p.path }) ? [p, other] : [other, p])
    } else {
      groups.push([p])
    }
  })

  return groups
}

function bodiesFor (contentVersion, patches, bases) {
  return groupPatches(patches).map(function (group) {
    return {
      paths: group.map(function (p) { return p.path }),
      body: group.some(function (p) { return p.invalid === true }) ? null : fieldsBody(contentVersion, group, bases)
    }
  })
}

/**
 * The bodies a Step 1 save sends: `changes` is rtaNominal.nominalChanges (`[{ field, path, value }]`). One body per field, except the point: a
 * drop on the map changes latitude AND longitude, and those two go in ONE body with two paths and two bases (P-S27). A change that cannot be sent
 * (not a request field) has `body: null`.
 *
 * @return {Array} [{ paths: [path, ...], body: {...} | null }] in the order of the changes
 */
export function requestBodies (contentVersion, changes, bases) {
  var patches = (Array.isArray(changes) ? changes : []).filter(function (c) { return !!c }).map(function (c) {
    var path = requestPathOf(c.field)

    return { path: path === null ? String(c.field) : path, value: c.value, invalid: path === null }
  })

  return bodiesFor(contentVersion, patches, bases)
}

/**
 * The bodies an option save sends: `changes` is rtaOption.contentChanges (`{ column: value }`). One body per column, except the pin: actualLat and
 * actualLng go in ONE body (P-S28, the pair rule of P-S27). A column outside the caller's list or server-owned has `body: null`.
 *
 * @return {Array} [{ paths: [column, ...], body: {...} | null }] in key order
 */
export function optionBodies (contentVersion, changes, bases, gates) {
  var patches = fieldPatches(changes).map(function (p) {
    return { path: p.path, value: p.value, invalid: !optionColumnOk(p.path, gates) }
  })

  return bodiesFor(contentVersion, patches, bases)
}

/**
 * A changed-keys object (rtaOption.contentChanges for an option) as patches in key order: [{ path, value }], one per key. (requestBodies /
 * optionBodies put a point's two in one body.)
 */
export function fieldPatches (changes) {
  var c = changes && typeof changes === 'object' ? changes : {}

  return Object.keys(c).map(function (path) { return { path: path, value: c[path] } })
}

/** A base map with the given paths' base moved to `version` (a field the server's row replaced). The input is not changed. */
export function adoptBases (bases, paths, version) {
  var out = {}
  var from = bases && typeof bases === 'object' ? bases : {}

  Object.keys(from).forEach(function (k) { out[k] = from[k] })
  ;(paths || []).forEach(function (p) { out[p] = version })

  return out
}

/** The base map of a row just read: each of `paths` at the row's contentVersion (every displayed value came from that response). */
export function seedBases (paths, version) {
  return adoptBases({}, paths, version)
}

/**
 * After a 2xx for `path`: does the answer hold the value we SENT, and where does the field's base go? requestRowSync.confirmSaved, fed this
 * module's base map: another user can write the same path between our commit and the answer's snapshot, and then the base may NOT move to the
 * answer's version (the next save must conflict). `savedRow` is the action (a request field) or the option row (an option column) as answered.
 *
 * @return {Object} { confirmed: Boolean, version: Number|undefined }
 */
export function savedBase (bases, path, sent, savedRow) {
  var entry = {}

  entry[path] = { baseVersion: bases && isNumber(bases[path]) ? bases[path] : undefined }

  return confirmSaved({ entries: entry }, path, sent, savedRow)
}
